import { NAVIGATION } from '@/app/navigation'

/**
 * Названия ресурсов прав, действий и сущностей журнала — словами, а не кодами.
 *
 * Источник один — навигация: пункт меню, который открывает ресурс, и называет его. Отдельный
 * словарь ресурсов разошёлся бы с меню при первом новом разделе. Где пунктов меню у ресурса
 * несколько (system.settings) или нет вовсе, берётся явная подпись `adm.resource.<код>`, а
 * если нет и её — сам код: незнакомый ресурс виден и чинится, пустое место живёт годами.
 */
type T = (key: string, params?: Record<string, string | number>) => string

const translated = (t: T, key: string): string | null => {
  const value = t(key)
  return value === key ? null : value
}

export function resourceLabel(t: T, resource: string): string {
  const own = translated(t, `adm.resource.${resource}`)
  if (own) return own
  for (const section of NAVIGATION) {
    const item = section.items.find((candidate) => candidate.resource === resource)
    if (item) return t(item.labelKey)
  }
  return resource
}

const SECTION_BY_PREFIX: Record<string, string> = {
  sales: 'nav.section.sales',
  billing: 'nav.section.billing',
  warehouse: 'nav.section.warehouse',
  hr: 'nav.section.hr',
  analytics: 'nav.section.analytics',
  payments: 'nav.section.payments',
  system: 'nav.section.admin',
}

/** Ключ раздела, в котором ресурс стоит в матрице прав. */
export function sectionKey(resource: string): string {
  const prefix = resource.split('.')[0] ?? ''
  return SECTION_BY_PREFIX[prefix] ?? 'adm.roles.group.other'
}

export const SECTION_ORDER = [...Object.values(SECTION_BY_PREFIX), 'adm.roles.group.other']

/** Порядок колонок матрицы; действия вне списка встают после него по алфавиту. */
export const ACTION_ORDER = ['read', 'create', 'update', 'delete', 'approve', 'export']

export function actionLabel(t: T, action: string): string {
  return translated(t, `adm.action.${action}`) ?? action
}

/**
 * Тип записи журнала — простое имя класса (`Deal`). Подпись — `adm.entity.<Имя>`, иначе
 * само имя: журнал пишет и сущности, которых нет в каталоге списков.
 */
export function entityTypeLabel(t: T, type: string): string {
  return translated(t, `adm.entity.${type}`) ?? type
}

/** Поле журнала: подпись пользовательского поля с сервера, затем колонка списка, затем имя. */
export function fieldLabel(t: T, field: string, serverLabel?: string | null): string {
  if (serverLabel) return serverLabel
  return translated(t, `column.${field}`) ?? field
}
